import type { ReactNode } from "react";
import { useLocation } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { useSeo, SITE_URL } from "@/lib/useSeo";
import { useLang } from "@/lib/useLang";
import { localize, stripEn } from "@/lib/locale";

type Props = {
  eyebrow: string;
  title: string;
  desc?: string;
  /** 검색엔진용 제목 (생략 시 title 사용) — 검색 키워드를 담은 짧은 제목 */
  seoTitle?: string;
  /** 검색엔진용 설명 (생략 시 desc 사용) */
  seoDescription?: string;
  /** 공유 미리보기 이미지 (절대 URL) */
  seoImage?: string;
  /** 기술자료 · 소식이면 "article" + 게시일(YYYY-MM-DD) */
  seoType?: "website" | "article";
  seoPublished?: string;
  noindex?: boolean;
  children: ReactNode;
};

// 빵부스러기(BreadcrumbList) 구조화 데이터용 상위 메뉴 이름. 첫 경로 조각 → 메뉴명
const SECTION_LABEL: Record<string, { ko: string; en: string }> = {
  about: { ko: "회사소개", en: "About" },
  services: { ko: "서비스", en: "Services" },
  solutions: { ko: "솔루션", en: "Solutions" },
  satellite: { ko: "위성통신", en: "Satellite" },
  notice: { ko: "공지사항", en: "Notice" },
  careers: { ko: "인재채용", en: "Careers" },
  downloads: { ko: "자료실", en: "Downloads" },
  privacy: { ko: "개인정보처리방침", en: "Privacy Policy" },
  terms: { ko: "이용약관", en: "Terms of Use" },
};

// 제목의 " — " 뒤(제품 이름, 예: Ocean-Seal™ · OCEAN-SUPPORT™)는 한 덩어리로 - 좁은 화면에서 하이픈 자리에서 끊기지 않게.
// 글자는 그대로라 H1 텍스트(검색엔진이 읽는 값)는 바뀌지 않는다
function titleNode(title: string): ReactNode {
  const i = title.lastIndexOf(" — ");
  if (i < 0) return title;
  return (
    <>
      {title.slice(0, i + 3)}
      <span className="whitespace-nowrap">{title.slice(i + 3)}</span>
    </>
  );
}

// 서브페이지 공통 틀: 네비게이션 + 제목 띠 + 본문 + 푸터 (+ 페이지별 SEO 메타 · 빵부스러기 구조화 데이터)
export default function PageLayout({ eyebrow, title, desc, seoTitle, seoDescription, seoImage, seoType, seoPublished, noindex, children }: Props) {
  useSeo({ title: seoTitle || title, description: seoDescription || desc || title, image: seoImage, noindex, type: seoType, published: seoPublished });
  const lang = useLang();
  const { pathname } = useLocation();

  // 홈 › 상위 메뉴 › (상세 페이지면) 현재 페이지. 검색 결과에 경로가 표시되도록 함 (영문 페이지는 /en 주소로)
  const en = lang === "en";
  const at = (path: string) => {
    const p = localize(path, en);
    return `${SITE_URL}${p === "/" ? "/" : p}`;
  };
  const segments = stripEn(pathname).split("/").filter(Boolean);
  const crumbs = [{ name: en ? "Home" : "홈", url: at("/") }];
  if (segments[0]) {
    const label = SECTION_LABEL[segments[0]];
    crumbs.push({ name: label ? label[lang] : title, url: at(`/${segments[0]}`) });
  }
  if (segments.length > 1) crumbs.push({ name: title, url: `${SITE_URL}${pathname}` });
  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: crumbs.map((c, i) => ({ "@type": "ListItem", position: i + 1, name: c.name, item: c.url })),
  };

  return (
    <div className="min-h-screen bg-background-50 text-foreground-900">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbLd) }} />
      <Navbar />
      <main className="pt-20">
        <header className="bg-navy-gradient text-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-10 py-14 md:py-20">
            <div className="text-[var(--brand-orange)] text-xs tracking-[0.3em] font-semibold mb-3 uppercase font-label">
              {eyebrow}
            </div>
            <h1 className="font-heading text-3xl md:text-5xl font-bold leading-tight">{titleNode(title)}</h1>
            {desc && (
              <p className="mt-4 max-w-2xl text-foreground-300 text-sm md:text-base leading-relaxed">
                {desc}
              </p>
            )}
          </div>
        </header>
        <div className="max-w-7xl mx-auto px-4 md:px-10 py-12 md:py-16">{children}</div>
      </main>
      <Footer />
    </div>
  );
}
